/**
 * Ядро методологии Kairo: расчёт дня вокруг сна и питания.
 *
 * Порядок именно такой и не может быть другим:
 *
 *   1. Сон и приёмы пищи занимают своё время — это фундамент, а не остаток.
 *   2. Встречи с фиксированным временем занимают то, что осталось.
 *   3. Свободное время — это то, что уцелело после первых двух пунктов.
 *   4. Задачам отдаётся лишь доля свободного времени (по умолчанию 80%),
 *      остальное — буфер: дорога, задержки, усталость, чужие просьбы.
 *
 * Модуль содержит только чистые функции без обращений к базе: их легко
 * покрыть тестами, а результат детерминирован при одних и тех же входных
 * данных.
 */

import type {
  CalendarEvent,
  Routine,
  RoutineKind,
  RoutineOverride,
  Task,
} from '@/shared/db/schema';
import { hasWeekday } from '@/shared/lib/weekdays';
import {
  formatDurationHours,
  type IsoDate,
  MINUTES_IN_DAY,
  type MinutesOfDay,
  freeSpans,
  isToday,
  mergeSpans,
  plural,
  spanOf,
  splitAtMidnight,
  type TimeSpan,
  totalMinutes,
  weekdayIndex,
} from '@/shared/lib/datetime';

/**
 * Задача, поставленная на время сна или еды.
 *
 * Её время считается в бюджете целиком: работа от наложения не исчезает.
 * Но у этих минут двойная роль — они уже вычтены из суток как сон или еда, и
 * они же заняты делом. Арифметикой это не выразить, не начав решать за
 * человека, ест он или работает, поэтому конфликт называется словами.
 */
export interface RoutineConflict {
  taskId: string;
  title: string;
  /** Слот, на который наехало дело: «обед», «сон». */
  slot: string;
  kind: RoutineKind;
  overlapMin: number;
}

/** Материализованный слот режима дня для конкретной даты. */
export interface RoutineOccurrence {
  routineId: string;
  kind: Routine['kind'];
  title: string;
  startMin: MinutesOfDay;
  durationMin: number;
  /** Слот сдвинут или изменён именно в этот день. */
  isModified: boolean;
  /** Переходит через полночь (актуально для сна). */
  crossesMidnight: boolean;
}

/** Активно ли правило в этот день недели. */
export function isRoutineActiveOn(routine: Routine, date: IsoDate): boolean {
  if (!routine.enabled) return false;
  return hasWeekday(routine.weekdaysMask, weekdayIndex(date));
}

/**
 * Разворачивает правила режима в слоты конкретного дня.
 *
 * Правила хранятся в единственном экземпляре, а конкретные дни вычисляются
 * здесь — иначе за год в базе оседало бы ~1800 однотипных записей на одного
 * пользователя. Отклонения («сегодня обед на час позже») лежат отдельно в
 * `RoutineOverride` и накладываются поверх правила.
 */
export function materializeRoutines(
  routines: Routine[],
  overrides: RoutineOverride[],
  date: IsoDate,
): RoutineOccurrence[] {
  const overrideByRoutine = new Map<string, RoutineOverride>();
  for (const override of overrides) {
    if (override.date === date && override.deletedAt === null) {
      overrideByRoutine.set(override.routineId, override);
    }
  }

  const result: RoutineOccurrence[] = [];

  for (const routine of routines) {
    if (routine.deletedAt !== null) continue;

    const override = overrideByRoutine.get(routine.id);

    // Пропуск в конкретный день: разгрузочный день, поездка, ночная смена.
    if (override?.skipped) continue;
    // Правило может быть неактивно в этот день недели — но явное отклонение
    // на дату сильнее расписания.
    if (!isRoutineActiveOn(routine, date) && !override) continue;

    const startMin = override?.startMin ?? routine.startMin;
    const durationMin = override?.durationMin ?? routine.durationMin;
    if (durationMin <= 0) continue;

    result.push({
      routineId: routine.id,
      kind: routine.kind,
      title: routine.title,
      startMin,
      durationMin,
      isModified: Boolean(override),
      crossesMidnight: startMin + durationMin > MINUTES_IN_DAY,
    });
  }

  return result.sort((a, b) => a.startMin - b.startMin);
}

/**
 * Отрезки, занятые слотом, в пределах суток.
 * Сон 23:00–07:00 даёт два куска: 23:00–24:00 и 00:00–07:00.
 */
export function occurrenceSpans(occurrence: RoutineOccurrence): TimeSpan[] {
  return splitAtMidnight(spanOf(occurrence.startMin, occurrence.durationMin));
}

/**
 * Бюджет времени на день — то, что показывается пользователю на экранах.
 *
 * Всё, что складывается в вердикт и в процент, считается **за календарный
 * день, от полуночи до полуночи**. База не зависит от того, когда человек
 * открыл экран: иначе одно и то же число уменьшалось бы само собой, «2 ч 40
 * мин · 147 %» через минуту превращалось бы в «2 ч 38 мин · 149 %», а сравнить
 * сегодня со вчера было бы нельзя вовсе.
 *
 * Отдельно лежит остаток суток (`restFreeMin`, `gaps`) — он-то и считается от
 * текущего момента: прошедший час занять уже нечем. Это ответ на другой
 * вопрос — «куда это ставить», а не «сколько дел помещается в день».
 */
export interface DayBudget {
  /** Минуты, занятые сном. */
  sleepMin: number;
  /** Минуты, занятые приёмами пищи. */
  mealsMin: number;
  /** Минуты, занятые встречами с фиксированным временем. */
  eventsMin: number;
  /** Минуты, занятые приёмами лекарств. */
  medsMin: number;
  /** Свободное время суток после сна, еды и встреч. */
  freeMin: number;
  /** Сколько из свободного разрешено занимать задачами (доля из профиля). */
  plannableMin: number;
  /** Резерв на непредвиденное — оставшаяся часть свободного времени. */
  bufferMin: number;
  /**
   * Суммарная оценка задач дня — и незакрытых, и уже выполненных.
   *
   * Выполненная задача — это потраченное время, а не исчезнувшее. Пока в счёт
   * шли только незакрытые, закрытие последнего дела возвращало остаток к
   * полному, и день, в котором всё сделано, объявлялся свободным.
   */
  plannedMin: number;
  /** Из них ещё предстоит. */
  todoMin: number;
  /** Из них уже сделано. */
  doneMin: number;
  /** Сколько задач дня ещё открыто. */
  todoCount: number;
  /** Сколько закрыто. Отменённые не считаются ни там, ни там. */
  doneCount: number;
  /** Насколько заполнен доступный бюджет: 0–1 норма, больше — перегруз. */
  loadRatio: number;
  /** Сколько ещё можно взять на день без перегруза. */
  remainingMin: number;
  /** С какой минуты день ещё можно занимать. Для не-сегодня — полночь. */
  fromMin: MinutesOfDay;
  /** Свободное время от `fromMin` до полуночи — сколько суток ещё осталось. */
  restFreeMin: number;
  /** Свободные промежутки начиная с `fromMin` — куда можно поставить задачу. */
  gaps: TimeSpan[];
  /** Дела, поставленные на время сна или еды, — их называют словами. */
  conflicts: RoutineConflict[];
}

interface ComputeBudgetParams {
  occurrences: RoutineOccurrence[];
  events: CalendarEvent[];
  tasks: Task[];
  /**
   * Приёмы лекарств: короткие обязательные интервалы из таблетницы.
   * Их время так же недоступно для задач, как время встречи.
   *
   * Поле обязательное намеренно: пока его можно было не передавать, два экрана
   * из трёх про него забыли, и один и тот же день имел три разных объёма
   * свободного времени — а автораскладка ставила задачу поверх приёма лекарства.
   * Пустой массив приходится писать явно, зато молча забыть больше нельзя.
   */
  doseSpans: TimeSpan[];
  /** Доля свободного времени под задачи (0–1). По умолчанию 0.8. */
  loadFactor?: number;
  /**
   * С какой минуты день ещё можно занимать — обычно `plannableFromMin(...)`.
   *
   * Влияет **только** на остаток суток (`restFreeMin`) и на свободные окна
   * (`gaps`): прошедший час уже ничем не занять. Знаменатель бюджета от него не
   * зависит — иначе один и тот же день имел бы столько разных бюджетов, сколько
   * экранов его показывает. Ровно это и случилось: 11,5 ч на календаре против
   * 2,5 ч на задачах, и противоположные вердикты по одному и тому же дню.
   */
  fromMin?: MinutesOfDay;
}

/**
 * С какой минуты день ещё можно планировать.
 *
 * Для сегодня — с текущего момента: прошедший час занять уже нечем. Для любого
 * другого дня — с полуночи, иначе выбор четверга вечером вторника делал бы
 * недоступным всё четверговое утро. Правило было записано в четырёх местах, и
 * в двух из них — без проверки дня.
 */
export function plannableFromMin(date: IsoDate, nowMin: MinutesOfDay): MinutesOfDay {
  return isToday(date) ? nowMin : 0;
}

/**
 * Считает бюджет дня.
 *
 * Пересекающиеся интервалы объединяются перед подсчётом: если встреча наложена
 * на обед, это по-прежнему один занятый отрезок, а не два. Без слияния
 * «занятое время» могло бы превысить сутки.
 *
 * Занятое время разбирается один раз на все сутки, и уже из готовых промежутков
 * вырезается остаток от `fromMin`. Так календарный день и остаток суток не
 * могут разойтись между собой — раньше это были два независимых расчёта.
 */
export function computeDayBudget({
  occurrences,
  events,
  tasks,
  doseSpans,
  loadFactor = 0.8,
  fromMin = 0,
}: ComputeBudgetParams): DayBudget {
  const wholeDay: TimeSpan = { start: 0, end: MINUTES_IN_DAY };

  const sleepSpans = occurrences.filter((o) => o.kind === 'sleep').flatMap(occurrenceSpans);
  const mealSpans = occurrences.filter((o) => o.kind === 'meal').flatMap(occurrenceSpans);
  const eventSpans = events
    .filter((event) => event.deletedAt === null)
    .map((event) => spanOf(event.startMin, event.durationMin));

  const clip = (spans: TimeSpan[]) =>
    spans
      .map((span) => ({
        start: Math.max(span.start, wholeDay.start),
        end: Math.min(span.end, wholeDay.end),
      }))
      .filter((span) => span.end > span.start);

  const sleepMin = totalMinutes(clip(sleepSpans));
  const mealsMin = totalMinutes(clip(mealSpans));

  const routineMerged = mergeSpans(clip([...sleepSpans, ...mealSpans]));

  /*
    Основа дня — сон, еда и приёмы лекарств. Это то, что человек не двигает и
    не планирует: оно просто занимает сутки. Приём лекарства часто назначен
    вплотную к еде и попадает внутрь её слота, поэтому считается только время,
    которое он добавляет сверх уже занятого.
  */
  const medsMin = totalMinutes(subtractSpans(clip(doseSpans), routineMerged));
  const baseBusy = mergeSpans([...routineMerged, ...clip(doseSpans)]);

  /*
    Свободное время — сутки за вычетом этой основы. Встречи из него не
    вычитаются: по методике 80% свободного идут под задачи и встречи вместе, а
    20% остаются буфером. Пока встреча вычиталась из базы наравне со сном, она
    не занимала бюджет, а уменьшала его: час совещания съедал час у задач и ещё
    пятнадцать минут у буфера — то есть стоил дню час пятнадцать вместо часа.
  */
  const freeMin = totalMinutes(freeSpans(baseBusy, wholeDay));

  /*
    Встреча, попавшая внутрь сна или приёма, второй раз не считается: обед,
    проведённый на совещании, — это один обед, а не обед плюс час.
  */
  const eventFreeSpans = subtractSpans(mergeSpans(clip(eventSpans)), baseBusy);
  const eventsMin = totalMinutes(eventFreeSpans);

  // Отменённая задача из счёта выпадает: от неё отказались, и держать её время
  // в бюджете значило бы вечно занижать остаток.
  const dayTasks = tasks.filter(
    (task) => task.deletedAt === null && task.status !== 'cancelled',
  );
  const minutesOf = (list: Task[]) =>
    list.reduce((sum, task) => sum + Math.max(0, task.estimateMin), 0);

  /*
    Задача с временем в сетке занимает конкретные часы дня — ровно как встреча.
    Задача без времени часов не занимает: её ещё предстоит куда-то поставить.
  */
  const placed = dayTasks.filter((task) => task.plannedStartMin !== null);
  const placedSpans = placed.map((task) =>
    spanOf(task.plannedStartMin!, Math.max(0, task.estimateMin)),
  );

  /*
    Окна для расстановки — про физическое время, а не про бюджет: поставить
    задачу на час совещания нельзя, сколько бы бюджета ни осталось. Занятое
    время образуют и встречи, и уже поставленные задачи — иначе расстановка
    сама кладёт дело поверх дела.
  */
  const occupied = mergeSpans([...baseBusy, ...clip(eventSpans), ...clip(placedSpans)]);
  const dayGaps = freeSpans(occupied, wholeDay);

  // Остаток суток вырезается из тех же промежутков: то же занятое время, только
  // без прошедших часов.
  const start = Math.min(Math.max(fromMin, 0), MINUTES_IN_DAY);
  const gaps = dayGaps
    .map((gap) => ({ start: Math.max(gap.start, start), end: gap.end }))
    .filter((gap) => gap.end > gap.start);
  const restFreeMin = totalMinutes(gaps);

  const plannableMin = Math.round(freeMin * loadFactor);
  const bufferMin = freeMin - plannableMin;

  const todo = dayTasks.filter((task) => task.status !== 'done');
  const done = dayTasks.filter((task) => task.status === 'done');
  const todoMin = minutesOf(todo);
  const doneMin = minutesOf(done);

  /*
    Занятое дело — это час дня, а не строка в списке, и час считается один раз.

    Пока бюджет складывал оценки задач с длительностью встреч, задача,
    поставленная на время совещания, стоила дню два часа вместо одного: остаток
    таял вдвое быстрее реального, а вердикт объявлял перегруз там, где его нет.
    Поэтому дела с временем в сетке сводятся в общие отрезки, и наложение
    считается единожды — задачей поверх встречи или задачей поверх задачи.

    Но основа дня из времени задачи не вычитается, в отличие от встречи. Час,
    поставленный на обед, человек всё равно потратит: он либо ест на ходу, либо
    двигает обед — работа от этого не исчезает. Со встречей иначе: обед,
    проведённый на совещании, остаётся одним обедом.

    Задачи без времени складываются как раньше: часа у них нет, накладываться не
    на что, и оценка входит в счёт целиком.
  */
  const committedMin = totalMinutes(mergeSpans([...clip(placedSpans), ...eventFreeSpans]));

  /*
    Конфликты ищутся только у задач. У встречи правило другое: обед, проведённый
    на совещании, остаётся одним обедом, лишнего с человека не списывается — и
    предъявлять нечего.
  */
  const conflicts: RoutineConflict[] = [];
  for (const task of placed) {
    const span = spanOf(task.plannedStartMin!, Math.max(0, task.estimateMin));
    let worst: RoutineConflict | null = null;

    for (const occurrence of occurrences) {
      const overlapMin = occurrenceSpans(occurrence).reduce(
        (sum, slot) =>
          sum + Math.max(0, Math.min(slot.end, span.end) - Math.max(slot.start, span.start)),
        0,
      );
      if (overlapMin === 0) continue;

      // Дело может наехать сразу на два слота — называем тот, куда оно попало
      // основательнее: перечислять все значило бы утопить смысл в подробностях.
      if (worst === null || overlapMin > worst.overlapMin) {
        worst = {
          taskId: task.id,
          title: task.title,
          slot: occurrence.title.toLocaleLowerCase('ru'),
          kind: occurrence.kind,
          overlapMin,
        };
      }
    }

    if (worst) conflicts.push(worst);
  }
  const plannedMin = committedMin + minutesOf(dayTasks.filter((t) => t.plannedStartMin === null));

  return {
    sleepMin,
    mealsMin,
    eventsMin,
    medsMin,
    freeMin,
    plannableMin,
    bufferMin,
    plannedMin,
    todoMin,
    doneMin,
    todoCount: todo.length,
    doneCount: done.length,
    loadRatio: plannableMin > 0 ? plannedMin / plannableMin : plannedMin > 0 ? Infinity : 0,
    remainingMin: plannableMin - plannedMin,
    fromMin: start,
    restFreeMin,
    gaps,
    conflicts,
  };
}

/** Вычитает из набора отрезков другой набор. */
function subtractSpans(source: TimeSpan[], subtract: TimeSpan[]): TimeSpan[] {
  const merged = mergeSpans(subtract);
  const result: TimeSpan[] = [];

  for (const span of source) {
    let pieces: TimeSpan[] = [span];
    for (const cut of merged) {
      const next: TimeSpan[] = [];
      for (const piece of pieces) {
        if (cut.end <= piece.start || cut.start >= piece.end) {
          next.push(piece);
          continue;
        }
        if (cut.start > piece.start) next.push({ start: piece.start, end: cut.start });
        if (cut.end < piece.end) next.push({ start: cut.end, end: piece.end });
      }
      pieces = next;
    }
    result.push(...pieces);
  }

  return result;
}

/** Оценка загрузки дня — определяет цвет и текст индикатора. */
export type LoadLevel = 'empty' | 'light' | 'balanced' | 'tight' | 'overloaded';

export function getLoadLevel(budget: DayBudget): LoadLevel {
  if (budget.plannedMin === 0) return 'empty';
  if (budget.loadRatio > 1) return 'overloaded';
  if (budget.loadRatio > 0.85) return 'tight';
  if (budget.loadRatio > 0.4) return 'balanced';
  return 'light';
}

export type LoadTone = 'neutral' | 'success' | 'warning' | 'danger';

export const LOAD_LEVEL_META: Record<
  LoadLevel,
  { label: string; hint: string; tone: LoadTone }
> = {
  empty: {
    label: 'День свободен',
    hint: 'Запланируйте задачи из списка',
    tone: 'neutral',
  },
  light: {
    label: 'Лёгкий день',
    hint: 'Есть запас — можно взять ещё',
    tone: 'success',
  },
  balanced: {
    label: 'Сбалансированный день',
    hint: 'Успеваете с запасом на непредвиденное',
    tone: 'success',
  },
  tight: {
    label: 'Плотный день',
    hint: 'Буфер почти израсходован',
    tone: 'warning',
  },
  overloaded: {
    label: 'Перегруз',
    hint: 'Задач больше, чем времени. Перенесите часть на завтра',
    tone: 'danger',
  },
};

/** Готовый вердикт по дню: экраны его только показывают, но не сочиняют. */
export interface DayLoadVerdict {
  level: LoadLevel;
  tone: LoadTone;
  /** Короткая метка для значка: «Лёгкий день», «Перегруз». */
  badge: string;
  /** Главная строка. Всегда про остаток: сколько ещё можно взять. */
  title: string;
  /**
   * Та же мысль одним числом: «Осталось 3,5 ч». Для свёрнутой шапки, где
   * рядом стоит плашка уровня и места на «под задачи и встречи» нет.
   */
  headline: string;
  /** Откуда взялся остаток: база бюджета и процент — только рядом с ней. */
  detail: string;
  /** Сколько суток осталось. `null`, когда считаем не от текущего момента. */
  restNote: string | null;
  /**
   * Дела, стоящие на времени сна или еды. `null` — конфликтов нет.
   *
   * Отдельной строкой, а не поправкой к числам: приложение не берётся решать,
   * ест человек или работает, но обязано показать, что он поставил одно на
   * другое.
   */
  conflictNote: string | null;
  /**
   * Продвижение по задачам дня: «Сделано 2 из 3».
   *
   * Одна цифра, отвечающая на вопрос «сколько я сегодня уже сделал». `null`,
   * когда задач на день нет вовсе — считать нечего.
   */
  progress: string | null;
}

/**
 * Формулирует вердикт по дню — единственное место, где это происходит.
 *
 * Раньше вердикт сочиняли две карточки независимо, и на одинаковых данных они
 * говорили разное. Теперь обе берут его отсюда.
 *
 * Формулировка идёт от остатка, а не от процента. «Осталось 1,5 ч под задачи» —
 * это ответ, с которым можно что-то сделать; «147 % бюджета» требует сначала
 * вспомнить, от чего считались проценты. Процент остался, но стоит вплотную к
 * своей базе, чтобы его нельзя было прочитать в отрыве от неё.
 */
export function describeDayLoad(budget: DayBudget): DayLoadVerdict {
  const level = getLoadLevel(budget);
  const meta = LOAD_LEVEL_META[level];

  const base = formatDurationHours(budget.plannableMin);
  const percent = Number.isFinite(budget.loadRatio) ? Math.round(budget.loadRatio * 100) : null;

  const detail =
    level === 'empty'
      ? `Из ${base} доступных не занято ничего`
      : `Из ${base} доступных занято ${formatDurationHours(budget.plannedMin)}${
          percent === null ? '' : ` — ${percent}% бюджета`
        }`;

  const allDone = budget.todoCount === 0 && budget.doneCount > 0;

  const title = allDone
    ? // День, где закрыто всё, — не пустой день. Пока выполненные задачи не шли
      // в счёт, здесь стояло «Свободно 11,5 ч под задачи»: сделанное просто
      // пропадало с экрана.
      'Всё на сегодня сделано'
    : level === 'empty'
      ? `Свободно ${base} под задачи и встречи`
      : budget.remainingMin < 0
        ? `Дел на ${formatDurationHours(-budget.remainingMin)} больше бюджета`
        : `Осталось ${formatDurationHours(budget.remainingMin)} под задачи и встречи`;

  const headline = allDone
    ? title
    : level === 'empty'
      ? `Свободно ${base}`
      : budget.remainingMin < 0
        ? `Сверх бюджета ${formatDurationHours(-budget.remainingMin)}`
        : `Осталось ${formatDurationHours(budget.remainingMin)}`;

  const total = budget.todoCount + budget.doneCount;

  return {
    level,
    // Закрытый день — успех, каким бы плотным он ни был.
    tone: allDone ? 'success' : meta.tone,
    badge: allDone ? 'День закрыт' : meta.label,
    title,
    headline,
    detail,
    progress: total > 0 ? `Сделано ${budget.doneCount} из ${total}` : null,
    // Для завтрашнего дня остаток суток совпадает с самими сутками — писать
    // «до конца дня свободно» про день, который ещё не начался, незачем.
    restNote:
      budget.fromMin > 0 ? `До конца дня свободно ${formatDurationHours(budget.restFreeMin)}` : null,
    conflictNote: describeConflicts(budget.conflicts),
  };
}

/**
 * Называет наложения дел на режим дня.
 *
 * Одно дело называется по имени: этого достаточно, чтобы его найти. Несколько —
 * считаются, а не перечисляются: список из пяти названий в строке под вердиктом
 * читать никто не станет, а «сколько и где» отвечает на вопрос целиком.
 *
 * В строке стоит род занятия — сон или еда, — а не название слота. Склонять
 * название приклеенной буквой нельзя: «Сон» так превращался в «стоит на соне», а
 * перекус человек и вовсе назовёт как захочет, и «Второй завтрак» стал бы
 * «второй завтраке». Какой именно слот задет, видно в самом дне: дело стоит
 * поверх него.
 */
function describeConflicts(conflicts: readonly RoutineConflict[]): string | null {
  if (conflicts.length === 0) return null;

  const kinds = new Set(conflicts.map((conflict) => conflict.kind));
  const where = kinds.size > 1 ? 'сна и еды' : kinds.has('sleep') ? 'сна' : 'еды';

  const [single] = conflicts;
  if (conflicts.length === 1 && single) return `«${single.title}» стоит на времени ${where}`;

  const count = conflicts.length;
  return `${count} ${plural(count, 'задача', 'задачи', 'задач')} ${plural(count, 'стоит', 'стоят', 'стоят')} на времени ${where}`;
}

/**
 * Подбирает ближайший свободный промежуток нужной длины.
 * Используется кнопкой «поставить в календарь»: задача сама находит место,
 * не заставляя пользователя вручную искать окно между делами.
 */
export function findFreeSlot(
  gaps: TimeSpan[],
  durationMin: number,
  afterMin: MinutesOfDay = 0,
): MinutesOfDay | null {
  for (const gap of gaps) {
    const start = Math.max(gap.start, afterMin);
    if (gap.end - start >= durationMin) return start;
  }
  return null;
}
